'use client';

import type { ReactNode } from 'react';
import { BLOCKS, MARKS, type Document } from '@contentful/rich-text-types';
import { useLayoutContext } from '@/components/Layout/LayoutProvider';
import { localizeInCityToken } from '@/lib/utils/localizeInCityToken';
import { useOptionalPaidLandingPageProvider } from '@/app/ready/[...slug]/PaidLandingPageProvider';
import type { OfferCardData } from './types';

const formatPhoneHref = (phone?: string | null) => {
  if (!phone) return '';
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
};

const hasRichTextContent = (value?: string | Document | null): value is Document => {
  if (!value || typeof value === 'string') {
    return false;
  }

  return value.content?.some((node) =>
    node.content?.some(
      (child) => child.nodeType === 'text' && 'value' in child && Boolean(child.value?.trim()),
    ),
  );
};

export function usePLPOfferCardViewModel(data: OfferCardData) {
  const { callRailPhone } = useLayoutContext();
  const plpContext = useOptionalPaidLandingPageProvider();

  const localizedCity = plpContext?.locationData?.locality ?? null;

  const title = hasRichTextContent(data.alternativeTitle) ? data.alternativeTitle : data.title;

  const teaser = hasRichTextContent(data.alternativeTeaser) ? data.alternativeTeaser : data.teaser;

  const offerTitleRichTextData =
    title && typeof title !== 'string'
      ? {
          content: title,
          className:
            "text-primary-500 font-bold leading-[110%] font-['Instrument_Sans'] text-[32px] mb-0!",
          options: {
            renderMark: {
              [MARKS.BOLD]: (text: ReactNode) => (
                <strong className='text-hero-text-highlight'>{text}</strong>
              ),
            },
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_node: unknown, children: ReactNode) => <h2>{children}</h2>,
            },
            renderText: (text: string) => localizeInCityToken(text, localizedCity),
          },
        }
      : null;

  const offerTitleText =
    typeof title === 'string' ? localizeInCityToken(title, localizedCity) : null;

  const teaserClassName = '[&_p]:font-medium [&_p]:text-[18px] [&_p]:leading-[160%] [&_p]:mb-0';

  const offerTeaserRichTextData =
    teaser && typeof teaser !== 'string'
      ? {
          content: teaser,
          className: teaserClassName,
          options: {
            renderText: (text: string) => localizeInCityToken(text, localizedCity),
          },
        }
      : null;

  const offerTeaserText =
    typeof teaser === 'string' ? localizeInCityToken(teaser, localizedCity) : null;

  return {
    availabilityBadge: data.availabilityBadge,

    offerTitleRichTextData,
    offerTitleText,

    offerTeaserRichTextData,
    offerTeaserText,

    callRailPhone,
    phoneHref: formatPhoneHref(callRailPhone),

    secondaryCtaLabel: callRailPhone,
    primaryCtaLabel: 'Book Now',
  };
}
